import { type ApolloError, useLazyQuery } from '@apollo/client';
import { audienceSegmentTagGroupOperator } from '@openx/components-deal-prompt/lib/constants';
import type { UseDealPromptAudienceSearchTextFetch } from '@openx/components-deal-prompt/lib/utils/context/type';
import { mapAudienceCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers';
import type { DealPromptFilters } from '@openx/components-deal-prompt/lib/utils/types';
import type { AudienceSegmentCombinedResult, AudienceSegmentSearchInputDescriptor } from '@openx/types/gqlTypes';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { useCallback } from 'react';
import { GetAudienceSegmentSearchTextDocument } from '../../../graphql/query/dealPrompt/GetAudienceSegmentSearchText.generated';
import { normalizeAudienceSegmentCombinedResult } from './normalizeAudienceSegmentSets';

export const useDealPromptAudienceSearchTextFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptAudienceSearchTextFetch => {
  const [fetchAudienceSegmentSearchText] = useLazyQuery(GetAudienceSegmentSearchTextDocument);

  const fetchDealPromptAudienceSearchText = useCallback(
    async (
      { query }: { query: string },
      abortSignal?: AbortSignal
    ): Promise<{
      generatedSearchInput: AudienceSegmentSearchInputDescriptor;
      combinedResult: AudienceSegmentCombinedResult;
      _error?: ApolloError;
    }> => {
      const result = await fetchAudienceSegmentSearchText({
        variables: {
          input: {
            inputText: query,
            filters: mapAudienceCriteriaToFiltersBody(criteria),
          },
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      const generatedSearchInput = result?.data?.audienceSegmentSearchText?.generatedSearchInput ?? {
        tagGroups: { operator: audienceSegmentTagGroupOperator.AND, groups: [] },
        filters: {},
      };

      const combinedResult = result?.data?.audienceSegmentSearchText?.combinedResult ?? {
        segments: [],
        segmentsSets: [],
      };

      return {
        generatedSearchInput,
        combinedResult: normalizeAudienceSegmentCombinedResult(combinedResult),
        _error: result?.error,
      };
    },
    [fetchAudienceSegmentSearchText, criteria]
  );

  return {
    fetchDealPromptAudienceSearchText,
  };
};
